import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import kebabCase from 'lodash/kebabCase'
import { Duration } from 'luxon'
import { InView } from 'react-intersection-observer'
import { PlayIcon } from '@/atoms/Icons/PlayIcon'
import { Image } from '@/atoms/Image'
import { CaptionMD, CaptionSM, TitleXS } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { publisher } from '@/constants/seo'
import { VideoObjectJsonLd } from '@/layout/Seo/JsonLd'
import { AngelPlayer, createAsset, SmpteController } from '@/organisms/AngelPlayer'
import { incrementUserViewCount } from '@/organisms/GetAppBanner/GetAppBannerUtil'
import { Note } from '@/services/StoriesService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { getDisplayCountry } from '@/utils/DisplayCountryUtil'
import { useLocale } from '@/utils/LocaleUtil/LocaleUtil'
import { useSafeTrack } from '@/utils/analytics'
import { useFormattedPublishDate } from '@/utils/date-utils'
import { useTranslate } from '@/utils/translate/translate-client'
import { WatchProjectContext } from '@/views/WatchProjectView/WatchProjectContext'

export interface CommunityVideoCardProps {
  story?: Note
  className?: string
}

export const CommunityVideoCard: React.FC<CommunityVideoCardProps> = ({ story, className }) => {
  const { locale } = useLocale()
  const { t } = useTranslate('watch')
  const track = useSafeTrack()
  const [showPlayer, setShowPlayer] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)
  const { id, publishedDate, totalLikes, authorLocation, user, video } = story as Note
  const storyId = id

  const { projectTheme } = useContext(WatchProjectContext)
  const [smtpeController, setSmtpeController] = useState<SmpteController | undefined>()

  const storyLink = `/watch/${projectTheme.slug}/community/stories/${storyId}`

  const handleControllerInit = useCallback((smtpeController: SmpteController) => {
    setSmtpeController(smtpeController)
  }, [])

  const viewerName = user?.firstName ?? t('viewer', 'Viewer')
  const authorCountry = authorLocation ?? user?.country
  let locationDisplay = ''

  if (authorCountry && authorCountry.length === 2) {
    locationDisplay = t('fromCountry', ' from {{country}}', { country: getDisplayCountry(authorCountry, locale) })
  }
  const durationDisplay = video?.source?.duration
    ? (() => {
        const d = Duration.fromObject({ seconds: video.source.duration }).normalize()
        const hours = Math.floor(d.as('hours'))
        if (hours >= 1) return ` ${hours}h`
        const minutes = Math.max(1, Math.floor(d.as('minutes')))
        return ` ${minutes}m`
      })()
    : ''

  const handleVideoStart = useCallback(() => {
    track('Community Video Content Started', {
      title: video?.title ?? 'Video',
      video_type: 'video',
    })
    incrementUserViewCount()
  }, [track, video?.title])

  useEffect(() => {
    if (!showPlayer || !smtpeController) return

    const handleClickOutside = (event: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
        smtpeController.pause()
        setShowPlayer(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showPlayer, smtpeController])

  return (
    <div
      ref={cardRef}
      aria-label="community-video-card"
      id={storyId}
      className="relative min-w-[250px] pr-6 lg:min-w-[316px]"
    >
      <div className={classNames('flex flex-col', className)}>
        {video?.posterCloudinaryPath && !showPlayer && (
          <div
            className="aspect-h-3 aspect-w-2 relative mb-3 w-full cursor-pointer overflow-hidden rounded-2xl"
            onClick={() => setShowPlayer(true)}
          >
            <div className="absolute inset-0">
              <Image
                alt={video?.title || video?.name || t('communityVideo', 'Community Video')}
                src={getCloudinaryImageUrl({ path: video?.posterCloudinaryPath })}
                width={316}
                height={474}
                className="h-full w-full object-cover"
              />
            </div>
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
              <div className="absolute left-1/2 top-1/2 flex h-[60px] w-[60px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/20 backdrop-blur-md">
                <PlayIcon size={30} />
              </div>
            </div>
          </div>
        )}
        {video?.source?.url && showPlayer && (
          <>
            {video.name && (
              <VideoObjectJsonLd
                contentUrl={video.source.url}
                description={video.subtitle}
                duration={Duration.fromDurationLike({ hours: 0, minutes: 0, seconds: video.source.duration })
                  .normalize()
                  .toISO()}
                interactionStatistic={{
                  type: 'InteractionCounter',
                  interactionType: { type: 'LikeAction' },
                  userInteractionCount: totalLikes,
                }}
                keyOverride={`VideoObject-${kebabCase(video.name)}`}
                name={video.name}
                publisher={publisher}
                thumbnailUrl={`https://images.angelstudios.com/image/upload/${video.posterCloudinaryPath}.webp`}
                uploadDate={publishedDate}
                url={`${paths.getBaseURL()}${storyLink}`}
              />
            )}
            <InView
              onChange={(inView) => {
                if (!inView) {
                  smtpeController?.pause()
                  setShowPlayer(false)
                }
              }}
            >
              <div className="!aspect-h-3 !aspect-w-2 relative mb-3 flex w-full items-center justify-center overflow-hidden rounded-2xl">
                <AngelPlayer
                  autoplay
                  asset={createAsset({
                    projectSlug: '',
                    guid: video.guid,
                    name: video.name,
                    url: video.source.url,
                    poster: video.posterCloudinaryPath,
                    title: '', // Set Title and Subtitle to blank string as it gets cut off in card format
                    subtitle: '',
                    slug: video.slug,
                    duration: video.source.duration,
                    videoType: 'story',
                  })}
                  shouldSaveWatchPosition={false}
                  onControllerInit={handleControllerInit}
                  overlays={undefined}
                  playerContainerClassName="w-full h-full flex items-center justify-center"
                  className="flex h-full w-full items-center justify-center bg-core-gray-800 object-cover"
                  aspectClassName="!aspect-w-2 !aspect-h-3 rounded-2xl"
                  onStart={handleVideoStart}
                />
              </div>
            </InView>
          </>
        )}
        <div className="flex flex-col gap-y-1">
          <div className="flex flex-row items-center justify-between">
            <CaptionMD weight="bold" className="whitespace-nowrap">{`${viewerName}${locationDisplay}`}</CaptionMD>
            <CaptionSM weight="bold" color="core-gray-500">
              {durationDisplay}
            </CaptionSM>
          </div>
          <CaptionSM color="core-gray-500">{useFormattedPublishDate(publishedDate)}</CaptionSM>
          <TitleXS weight="bold" className="overflow-hidden text-ellipsis whitespace-nowrap">
            {video?.title}
          </TitleXS>
          <CaptionSM color="core-gray-500" className="line-clamp-4 min-h-16">
            {video?.subtitle}
          </CaptionSM>
        </div>
      </div>
    </div>
  )
}
